import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { Button, Empty, Table, Tag, Typography } from 'antd';
import loadingSVG from '../../assets/loading.svg';
import moment from 'moment';
import { ColumnType } from 'antd/lib/table/interface';
import { Notification } from '../../interfaces/api/Notifications';
import { getChannelReadableName } from '../../utils/readable';
import { NotificationDetailsStatus } from '../../interfaces/api/Logs';
import { Channels } from '../common/channelsEnum';
import { User } from '../../interfaces/backend/User';
import { Spin } from 'antd/lib';
import { getChannelIcon } from '../notifications/NotificationsContainer';
import { Template } from '../../interfaces/backend/Template';
import { RedoOutlined } from '@ant-design/icons';

const { Text } = Typography;

export const getStatusName = (status: string | undefined): string => {
  switch (status) {
    case 'SUCCESS':
      return 'Successful';
    case 'FAILURE':
      return 'Failed';
    case 'RECEIVED':
      return 'Processing';
    default:
      return '';
  }
};

export const getStatusDescription = (status: string | undefined): string => {
  switch (status) {
    case 'SUCCESS':
      return 'No errors encountered.';
    case 'FAILURE':
      return 'Something unexpected has happened.';
    case 'RECEIVED':
      return 'The notification is being processed.';
    default:
      return '';
  }
};

export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'SUCCESS':
      return 'green';
    case 'FAILURE':
      return 'red';
    case 'RECEIVED':
      return 'blue';
    default:
      return 'orange';
  }
};

export type LogRow = {
  trackingId: string;
  user: User;
  status: string;
  notificationId: string;
  channels: string[];
  timestamp: number;
  request: string;
  envId: string;
};

export type log = {
  timestamp: number;
  message: {
    notificationId?: string;
    error?: {
      error: {
        code?: string | number;
        errno?: number;
        syscall?: string;
        sourceUrl?: string;
        name?: string;
        message?: string;
        statusCode?: number;
        headers?: Record<string, string>;
        body?: string;
        endpoint?: string;
      };
    };
    batchingChannels?: Channels[];
    batchTrackingId?: string;
    batched?: Record<string, string>;
    batchSchedule?: string;
    message?:
      | {
          bounce?: {
            feedbackId: string;
            bounceType: string;
            bounceSubType: string;
            bouncedRecipients: {
              emailAddress: string;
              action: string;
              status: string;
              diagnosticCode: string;
            }[];
            timestamp: string;
            reportingMTA: string;
          };
          complaint?: {
            complaintSubType: string;
            complainedRecipients: {
              emailAddress: string;
            }[];
            timestamp: string;
            feedbackId: string;
            userAgent: string;
            complaintFeedbackType: string;
            arrivalDate: string;
          };
          from?: string;
          to?: string[];
          replyTo?: string[];
          cc?: string[];
          bcc?: string[];
          subject?: string;
          html?: string;
          attachments?: { filename: string; href: string }[];
          headers?: { key: string; value: string }[];

          ses?: {
            ConfigurationSetName: string;
          };
          eventType?: string;
          mail?: {
            timestamp: string;
            source: string;
            sourceArn?: string;
            sendingAccountId?: string;
            tags?: { [key: string]: string[] };
            messageId: string;
            destination: string[];
            headersTruncated: boolean;
            headers: {
              name: string;
              value: string;
            }[];
            commonHeaders: {
              returnPath?: string;
              from?: string[] | undefined;
              date?: string;
              to?: string[] | undefined;
              cc?: string[] | undefined;
              bcc?: string[] | undefined;
              sender?: string[] | undefined;
              replyTo?: string[] | undefined;
              messageId: string;
              subject?: string | undefined;
            };
          };
          delivery?: {
            timestamp: string;
            processingTimeMillis: number;
            recipients: string[];
            smtpResponse: string;
            reportingMTA: string;
          };
        }
      | string;
    From?: string;
    SmsStatus?: string;
    To?: string;
    ErrorCode?: string;
    CallStatus?: string;
    CallDuration?: string;
    Called?: string;
    CalledCountry?: string;
    CalledState?: string;
    Caller?: string;
    CallerCity?: string;
    CallerCountry?: string;
    CallerState?: string;
    CallerZip?: string;
    FromCity?: string;
    FromCountry?: string;
    FromState?: string;
    FromZip?: string;
    SipResponseCode?: string;
    SequenceNumber?: string;
    ToCity?: string;
    ToCountry?: string;
    ToState?: string;
    ToZip?: string;
    envId?: string;
    status: NotificationDetailsStatus;
    //ToDo: we should have only one channels or channel
    channels?: Channels[];
    body?: {
      title?: string;
      redirectURL?: string;
      imageURL?: string;
    };
    text?: string;
    userId?: string;
    receivingNumber?: string;
    sendingNumber?: string;
    template: Template;
    templates?:
      | { [key: string]: string }
      | { [key: string]: { [key: string]: string } };
    seen?: boolean;
    subNotificationId?: string | null;
    from?: string;
    to?: string[];
    replyTo?: string[];
    cc?: string[];
    bcc?: string[];
    subject?: string;
    html?: string;
    attachments?: { filename: string; href: string }[];
    headers?: { key: string; value: string }[];
    messages?: string[];
    ses?: {
      ConfigurationSetName: string;
    };
    eventType?: string;
    mail?: {
      timestamp: string;
      source: string;
      sourceArn?: string;
      sendingAccountId?: string;
      tags?: { [key: string]: string[] };
      messageId: string;
      destination: string[];
      headersTruncated: boolean;
      headers: {
        name: string;
        value: string;
      }[];
      commonHeaders: {
        returnPath?: string;
        from?: string[] | undefined;
        date: string;
        to?: string[] | undefined;
        cc?: string[] | undefined;
        bcc?: string[] | undefined;
        sender?: string[] | undefined;
        replyTo?: string[] | undefined;
        messageId: string;
        subject?: string | undefined;
      };
    };
    delivery?: {
      timestamp: string;
      processingTimeMillis: number;
      recipients: string[];
      smtpResponse: string;
      reportingMTA: string;
    };
    schedule?: string;
    updated?: Record<string, string>;
  };
};

export type LogStreamTableProps = {
  logs: LogRow[];
  loading: boolean;
  notifications: Notification[];
  timezone: string;
  openLogDetails: (trackingId: string) => void;
  setResending: Dispatch<
    SetStateAction<
      {
        trackingId: string;
        envId: string;
        request: string;
        response: string | undefined;
        error: unknown;
      }[]
    >
  >;
  setPageSize: Dispatch<SetStateAction<number>>;
  setCurrentPage: Dispatch<SetStateAction<number>>;
};

export default function LogStreamTable({
  logs,
  loading,
  notifications,
  timezone,
  openLogDetails,
  setResending,
  setPageSize,
  setCurrentPage
}: LogStreamTableProps): JSX.Element {
  const [columns, setColumns] = useState<ColumnType<LogRow>[]>();

  useMemo(() => {
    setColumns([
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (status: string, record) => {
          if (record.trackingId === 'loading') {
            return null;
          }
          return (
            <Tag color={getStatusColor(status)}>{getStatusName(status)}</Tag>
          );
        }
      },
      {
        title: 'User',
        dataIndex: 'userId',
        key: 'userId',
        render: (_text: string, record: LogRow) => {
          return (
            <div>
              <div key="userId">
                <Text>{record.user.id}</Text>
              </div>
              {record.user.id !== record.user.email && (
                <div key="email">
                  <Text type="secondary">{record.user?.email}</Text>
                </div>
              )}
              {record.user.id !== record.user.number && (
                <div key="number">
                  <Text type="secondary">{record.user?.number}</Text>
                </div>
              )}
            </div>
          );
        }
      },
      {
        title: 'Notification',
        key: 'notification',
        render: (text: string, record: LogRow) => {
          if (record.trackingId === 'loading') {
            return <Spin />;
          }
          let notificationTitle = notifications.find(
            (n) => n.notificationId === record.notificationId
          )?.title;

          if (notificationTitle === undefined) {
            notificationTitle = record.notificationId;
          }

          if (notificationTitle !== record.notificationId) {
            notificationTitle += ' (' + record.notificationId + ')';
          }
          if (record.channels && record.channels.length > 0) {
            return (
              <div>
                <div>
                  <Text>{notificationTitle}</Text>
                </div>
                {record.channels.map((c) => (
                  <div>
                    {getChannelIcon(c as Channels)}
                    <Text type="secondary" style={{ marginLeft: 8 }}>
                      {getChannelReadableName(c)}
                    </Text>
                  </div>
                ))}
              </div>
            );
          } else {
            return <span>{notificationTitle}</span>;
          }
        }
      },
      {
        title: `Timestamp (${moment().tz(timezone).format('z')})`,
        dataIndex: 'timestamp',
        key: 'timestamp',
        render: (timestamp: number, record) => {
          if (record.trackingId === 'loading') {
            return null;
          }
          return (
            <span>
              {moment(timestamp).tz(timezone).format('YYYY-MM-DD HH:mm:ss')}
            </span>
          );
        }
      },
      {
        title: 'Actions',
        dataIndex: 'actions',
        key: 'actions',
        render: (_, record) => {
          if (!record || record.trackingId === 'loading' || !record.user.id) {
            return null;
          }
          return (
            <Button
              icon={<RedoOutlined />}
              type="default"
              onClick={(e) => {
                setResending([
                  {
                    trackingId: record.trackingId,
                    envId: record.envId,
                    request: record.request,
                    response: undefined,
                    error: null
                  }
                ]);
                e.stopPropagation();
                return;
              }}
            >
              Resend
            </Button>
          );
        }
      }
    ]);
  }, [notifications, timezone, setResending]);

  return (
    <>
      <Table
        rowClassName="cursor-pointer"
        rowKey={(record) => record.trackingId}
        columns={columns}
        pagination={{
          defaultPageSize: 100,
          showSizeChanger: true,
          pageSizeOptions: ['50', '100', '250', '500', '1000']
        }}
        onChange={(pagination) => {
          setPageSize(pagination.pageSize || 100);
          setCurrentPage(pagination.current || 1);
          console.log({
            size: pagination.pageSize,
            page: pagination.current
          });
        }}
        onRow={(record) => {
          return {
            onClick: async () => {
              const trackingId = record?.trackingId?.split(':')[0];
              openLogDetails(trackingId);
            }
          };
        }}
        dataSource={
          loading
            ? [
                {
                  trackingId: 'loading',
                  status: '',
                  user: {
                    id: ''
                  },
                  notificationId: '',
                  channels: [],
                  timestamp: 0,
                  request: '',
                  envId: ''
                },
                ...logs
              ]
            : [...logs]
        }
        locale={{
          emptyText: loading ? (
            <Empty image={loadingSVG} description="" />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No logs to show. Change the filters or send some notifications!"
            />
          )
        }}
      />
    </>
  );
}
